import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { fileService } from '../services/fileService';
import { Company } from '../types/company';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const CompanyList: React.FC = () => {
  const { t } = useTranslation();
  const { data: companies, isLoading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: companyService.getCompanies
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t('common.error')}</p>
      </div>
    );
  }

  if (!companies || !Array.isArray(companies) || companies.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">У вас пока нет компаний</p>
        <Link
          to="/companies/new"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('companies.addCompany')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('companies.title')}</h2>
        <Link
          to="/companies/new"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('companies.addCompany')}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(companies) && companies.map((company) => (
          <CompanyCard key={company.id} company={company} />
        ))}
      </div>
    </div>
  );
};

const CompanyCard: React.FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          {company.logoPath && (
            <img
              src={fileService.getCompanyLogoUrl(company.logoPath)}
              alt={`${company.name} logo`}
              className="w-12 h-12 object-contain rounded border border-gray-200 bg-gray-50"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
        </div>
        <Link
          to={`/companies/${company.id}/edit`}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {t('common.edit')}
        </Link>
      </div>

      <div className="space-y-2 text-sm text-gray-600">
        <p><span className="font-medium">{t('companies.taxId')}:</span> {company.taxId}</p>
        {company.address && (
          <p><span className="font-medium">{t('companies.address')}:</span> {company.address}</p>
        )}
        {company.phone && (
          <p><span className="font-medium">{t('companies.phone')}:</span> {company.phone}</p>
        )}
        {company.email && (
          <p><span className="font-medium">{t('companies.email')}:</span> {company.email}</p>
        )}
      </div>

      {company.bankAccounts && company.bankAccounts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('companies.bankAccounts.title')}:</p>
          <div className="space-y-1">
            {company.bankAccounts.slice(0, 2).map((account) => (
              <p key={account.id} className="text-xs text-gray-600">
                {account.bankName} • {account.accountNumber} • {account.currency}
              </p>
            ))}
            {company.bankAccounts.length > 2 && (
              <p className="text-xs text-gray-500">
                +{company.bankAccounts.length - 2} еще
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <Link
          to={`/companies/${company.id}`}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {t('companies.details')} →
        </Link>
      </div>
    </div>
  );
};

export default CompanyList;
